/**
 * Small client-side helpers for document file attachments. The server is
 * the real gatekeeper (size + type are re-checked in the upload route);
 * these just keep the picker honest and the UI tidy.
 */

// Mirrors the server's ALLOWED_MIME (documents route). HEIC/HEIF come off
// iPhones; some browsers report them with an empty type, so we also
// accept by extension below.
const ACCEPTED_MIME = new Set([
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
]);

const ACCEPTED_EXT = ['.pdf', '.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif'];

/** `accept` attribute for the file input — PDFs and images. */
export const DOC_FILE_ACCEPT = 'application/pdf,image/*,.pdf,.heic,.heif';

/** Whether a picked/dropped file is an acceptable document file. */
export function isAcceptedDocFile(file: File): boolean {
  if (file.type && ACCEPTED_MIME.has(file.type.toLowerCase())) return true;
  const name = file.name.toLowerCase();
  return ACCEPTED_EXT.some((ext) => name.endsWith(ext));
}

const EXT_MIME: Record<string, string> = {
  pdf: 'application/pdf',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  heic: 'image/heic',
  heif: 'image/heif',
};

/**
 * The MIME type to send for an upload. iOS often hands back a File with
 * an empty `type` (notably HEIC/HEIF, and sometimes scanned PDFs), which
 * would otherwise be sent as `application/octet-stream` and rejected by
 * the server. Fall back to the extension so the right type goes up.
 */
export function inferMimeType(file: File): string {
  if (file.type) return file.type;
  const ext = file.name.split('.').pop()?.toLowerCase();
  return (ext && EXT_MIME[ext]) || 'application/octet-stream';
}

/** Human-readable file size, e.g. "1.4 MB". */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb < 10 ? kb.toFixed(1) : Math.round(kb)} KB`;
  const mb = kb / 1024;
  return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
}
